import type { ForgeModifiers, RunUpgradeDef } from '../types';

/**
 * Run-only upgrade catalogue. Cost is in gold, paid out of run-state.
 * `apply` mutates the run's forge modifiers, which the forge phase reads on
 * its next instantiation. Each upgrade can be picked at most once per run.
 */

export interface UpgradeContext {
  modifiers: ForgeModifiers;
}

export const UPGRADES: Record<string, RunUpgradeDef> = {
  reinforced_bellows: {
    id: 'reinforced_bellows',
    name: 'Reinforced Bellows',
    description: 'Heat target bands are 25% wider for the rest of this run.',
    cost: 25,
    apply: () => {
      /* hook applied via ctx in ShopPhase */
    },
  },
  sturdy_hammer: {
    id: 'sturdy_hammer',
    name: 'Sturdy Hammer',
    description: 'Strike Perfect window is 25% wider.',
    cost: 30,
    apply: () => {
      /* applied via ctx */
    },
  },
  quench_oil: {
    id: 'quench_oil',
    name: 'Quench Oil',
    description: 'Plunge timing windows are 30% wider.',
    cost: 25,
    apply: () => {
      /* applied via ctx */
    },
  },
  steel_ingot: {
    id: 'steel_ingot',
    name: 'Steel Ingot',
    description: 'Forged weapons deal +6 base damage.',
    cost: 40,
    apply: () => {
      /* applied via ctx */
    },
  },
  sharpening_stone: {
    id: 'sharpening_stone',
    name: 'Sharpening Stone',
    description: 'Forged weapons gain +15 base durability.',
    cost: 30,
    apply: () => {
      /* applied via ctx */
    },
  },
  lucky_tongs: {
    id: 'lucky_tongs',
    name: 'Lucky Tongs',
    description: '25% chance to start a forge already at perfect heat.',
    cost: 35,
    apply: () => {
      /* applied via ctx */
    },
  },
};

/** Applies an upgrade to a forge-modifier context. Keeps apply logic centralised. */
export function applyUpgradeToContext(id: string, ctx: UpgradeContext): void {
  switch (id) {
    case 'reinforced_bellows':
      ctx.modifiers.heatBandMult *= 1.25;
      break;
    case 'sturdy_hammer':
      ctx.modifiers.strikeWindowMult *= 1.25;
      break;
    case 'quench_oil':
      ctx.modifiers.quenchWindowMult *= 1.3;
      break;
    case 'steel_ingot':
      ctx.modifiers.bonusDamage += 6;
      break;
    case 'sharpening_stone':
      ctx.modifiers.bonusDurability += 15;
      break;
    case 'lucky_tongs':
      ctx.modifiers.luckyStart = Math.min(1, ctx.modifiers.luckyStart + 0.25);
      break;
  }
}

export const ALL_UPGRADE_IDS = Object.keys(UPGRADES);
